/** 手動登録の対象上限。個別選択リクエスト1回あたりのID数上限は別に500件。 */
export const MANUAL_TARGET_LIMIT = 10000;
export const MANUAL_FRIEND_IDS_LIMIT = 500;
export const PUBLISH_APPLY_RUN_LIMIT = 1000;
/** 1条件で選べるタグ数の上限。backendの SCENARIO_CONDITION_MAX_TAGS と揃える。 */
export const SCENARIO_CONDITION_MAX_TAGS = 10;

export type ApiIssue = { code?: string; details?: unknown; trigger?: unknown; limit?: unknown };

export const apiIssue = (error: unknown): ApiIssue => {
  const data = (error as { response?: { data?: unknown } } | null)?.response?.data;
  return data && typeof data === 'object' && !Array.isArray(data) ? (data as ApiIssue) : {};
};

export const apiIssueCode = (error: unknown) => apiIssue(error).code;

const messages: Record<string, string> = {
  ACCOUNT_UNAVAILABLE: 'LINE公式アカウントが停止中または連携解除済みのため実行できません。',
  ACTIVE_RUN_EXISTS: 'この友だちには進行中の配信があるため、あらためて開始できません。',
  ACTIVE_RUN_LIMIT_EXCEEDED: '既存Runへの反映は1,000件までです。反映をOFFにして公開してください。',
  AUTO_TRIGGER_RUN_LIMIT_EXCEEDED: 'このタグで始まる配信が一度に1,000件を超えるため、タグを付けられませんでした。タグ付与で始まるシナリオを減らすか、対象アカウントを絞ったシナリオに作り替えてください。',
  AUTO_TRIGGER_SCENARIO_LIMIT_EXCEEDED: '自動で開始するシナリオが上限の1,000本に達しています。不要なシナリオを停止またはアーカイブしてください。',
  CANCELLATION_PENDING: '配信の取消を処理中です。完了までしばらく待ってから再実行してください。',
  DEPENDENCY_CHECK_CAPACITY_EXCEEDED: '確認する配信が多すぎるため、いまは実行できません。しばらくしてから再実行してください。',
  DEPENDENCY_CONFLICT: '参照しているタグまたは素材を利用できません。選択内容を確認してください。',
  DEPENDENCY_IN_USE: 'ステップ配信で使用されているため削除できません。使用しているシナリオを確認してください。',
  DEPENDENCY_PROJECTION_MUTATION_LIMIT_EXCEEDED: '内部参照の更新が10,000件を超えるため実行できません。既存Runへの反映をOFFにするか、参照するタグ・素材を減らしてください。',
  DISPATCH_BUSY: '送信処理が混み合っています。同じ内容のまま、しばらくして再実行してください。',
  EDIT_VERSION_CONFLICT: '別の変更があります。再読み込みしてください。',
  INVALID_ASSET_PAYLOAD: '選択した素材の内容をLINEへ送信できません。素材を確認してください。',
  INVALID_CURSOR: '表示位置が古くなりました。先頭から読み込み直してください。',
  INVALID_DEFINITION: '配信内容に未入力または不正な項目があります。各ステップを確認してください。',
  INVALID_REQUEST: '入力内容を確認してください。',
  INVALID_STATE_TRANSITION: '現在の状態ではこの操作を実行できません。ページを再読み込みしてください。',
  INVALID_TEST_TOKEN: '確認内容が無効です。もう一度内容を確認してください。',
  MANUAL_PREVIEW_CHANGED: '対象件数が変わりました。最新件数を確認してください。',
  OPERATION_DEADLINE_EXCEEDED: '処理時間の上限に達しました。しばらくしてから再実行してください。',
  OWNERSHIP_MISMATCH: 'この操作を実行する権限がありません。ページを再読み込みしてください。',
  PUBLISH_PREVIEW_CHANGED: '進行中の配信件数が変わりました。最新件数を再確認してください。',
  RESUME_EXPIRED: '再開期限が切れました。最新の状態を表示しています。',
  SCENARIO_ARCHIVED: 'このシナリオはアーカイブされています。ページを再読み込みしてください。',
  TAG_LIMIT_EXCEEDED: '一度に指定できるタグは100件までです。件数を減らしてください。',
  TARGET_LIMIT_EXCEEDED: '対象人数が上限の10,000人を超えています。対象を絞り込んでください。',
  TEST_RECIPIENTS_CHANGED: 'テストユーザーの状態が変わったか、対象アカウントをフォローしていない友だちが含まれています。対象を選び直してください。',
  TEST_TOKEN_EXPIRED: '確認内容の有効期限が切れました。もう一度内容を確認してください。',
  TEST_TOKEN_TOO_LARGE: '確認内容が大きすぎます。対象人数またはメッセージ内容を減らしてください。',
};

const AUTO_TRIGGER_MUTATION_LIMIT_MESSAGE =
  'この構成では1回の自動開始で内部参照の更新が10,000件を超えます。シナリオ数または参照を減らしてください。';

/** HTTP statusだけで判断する画面が、同じcodeの文言を再定義せずに参照するための入口。 */
export const issueCodeMessage = (code: string) => messages[code];

/** `steps[0].messages[1].content` のようなfield pathを画面の言葉へ直す。 */
const definitionPlace = (path: string): string => {
  const step = /^steps\[(\d+)\]/.exec(path);
  if (!step) {
    if (path.startsWith('trigger.tagId')) return '開始条件のタグ';
    if (path.startsWith('trigger')) return '開始条件';
    if (path.startsWith('steps')) return 'ステップ';
    return '配信内容';
  }
  const base = `ステップ${Number(step[1]) + 1}`;
  const rest = path.slice(step[0].length);
  const message = /^\.messages\[(\d+)\]/.exec(rest);
  if (message) {
    const target = rest.slice(message[0].length);
    const field =
      target.startsWith('.content') ? 'の本文'
      : target.startsWith('.media') ? 'の画像・動画'
      : target.startsWith('.assetId') ? 'の素材'
      : '';
    return `${base}のメッセージ${Number(message[1]) + 1}${field}`;
  }
  if (rest.startsWith('.timing')) return `${base}の配信タイミング`;
  if (rest.startsWith('.condition')) return `${base}の送信条件`;
  if (rest.startsWith('.onReachTagId')) return `${base}の「送信成功時に付けるタグ」`;
  if (rest.startsWith('.falseTargetStepId')) return `${base}の「条件不成立時」の遷移先`;
  if (rest.startsWith('.messages')) return `${base}のメッセージ`;
  return base;
};

/** 場所（`definitionPlace`）へそのまま続けられる文にする。 */
const definitionReason = (reason: string): string => {
  if (/at least 1 and at most 5 messages/.test(reason)) return 'を1〜5件にしてください。';
  if (/at most 5 messages/.test(reason)) return 'は5件までです。';
  if (/must not be blank/.test(reason)) return 'が未入力です。';
  if (/is required/.test(reason)) return 'が未設定です。';
  return 'の内容を確認してください。';
};

/** INVALID_DEFINITIONのdetailsを、どこを直せばよいか分かる文へ変換する。 */
export const definitionIssueMessage = (details: unknown): string | undefined => {
  if (typeof details !== 'string' || !details) return undefined;
  const [path, ...rest] = details.split(': ');
  const reason = rest.join(': ');
  if (!reason) return undefined;
  // ステップ0本は場所を持たないので、この文だけ独立して出す
  if (/at least 1 step/.test(reason)) return 'ステップを1つ以上追加してください。';
  return `${definitionPlace(path)}${definitionReason(reason)}`;
};

// サーバー由来の日本語文言だけを通し、通信ライブラリ既定の英文
// （Not Found / Request failed with status code 503 等）はfallbackへ倒す
const localizedErrorMessage = (error: unknown): string | undefined => {
  const message = error instanceof Error ? error.message : undefined;
  return message && /[぀-ヿ㐀-䶿一-鿿]/.test(message) ? message : undefined;
};

export const issueMessage = (error: unknown, fallback: string) => {
  const issue = apiIssue(error);
  if (issue.code === 'DEPENDENCY_PROJECTION_MUTATION_LIMIT_EXCEEDED' && typeof issue.trigger === 'string') {
    return AUTO_TRIGGER_MUTATION_LIMIT_MESSAGE;
  }
  if (issue.code === 'INVALID_DEFINITION') {
    const detail = definitionIssueMessage(issue.details);
    if (detail) return detail;
  }
  return messages[issue.code ?? ''] ?? localizedErrorMessage(error) ?? fallback;
};
